/**
 * DASHBOARD-QUESTION-OPEN.WIDGET
 * Display open question with messages
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useEffect, useState } from "react"
import PageLoader from "@/components/page-loader"
import DashboardQuestionOpenSkeletonWidget, {
  DashboardQuestionOpenSummarySkeletonWidget
} from "./skeletons/dashboard-question-open-skeleton.widget"
import { Message, MESSAGE_CEIL_COUNT_SUMMARY } from "@/redux/message.types"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVING } from "@/redux/_status.types"
import { Question } from "@/redux/question.types"
import Card from "@/components/card"
import ListButton from "@/components/list-button"
import {
  faBell,
  faCaretDown,
  faCaretRight,
  faComment,
  faExclamationTriangle,
  faPen,
  faTimes
} from "@fortawesome/free-solid-svg-icons"
import Space from "@/components/space"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Topic } from "@/redux/topic.types"
import { orderBy } from "lodash"
import { Session } from "@/redux/_session.types"
import TextInput from "@/components/text-input"
import makeInsensitive from "@/utils/make-insensitive.utils"
import DashboardMessageWidget from "./dashboard-message.widget"
import Tabs from "@/components/tabs"
import { store } from "@/index"
import { FilterState } from "@/redux/filter.types"
import { fetchMessages } from "@/redux/_archive.actions"
import { SurveyState } from "@/redux/survey.types"
import { messageUpdateColor } from "@/redux/message.actions"
import ReactTooltip from "react-tooltip"
import { Tag } from "@/redux/account.types"
import ListItem from "@/components/list-item"
import EditTagsModal from "@/modals/edit-tags.modal"
import { accountUpdateColors } from "@/redux/account.actions"
import { sessionEditAccountColors } from "@/redux/_session.actions"
import Chip from "@/components/chip"
import DashboardMessagesCloudWidget from "./dashboard-messages-cloud.widget"
import DashboardSummarizeWidget from "./dashboard-summarize.widget"
import UpgradeWidget from "./upgrade.widget"
import { Population } from "@/redux/population.types"
import { getSegmentationPopulations } from "@/utils/get-segmentation-populations.utils"
import Link from "@/components/link"
import Modal from "@/components/modal"
import Checkbox from "@/components/checkbox"
import { questionUpdatePrompt } from "@/redux/question.actions"
import { projectFetchOne } from "@/redux/project.actions"
import { toast } from "react-toastify"

interface StateProps extends WithTranslation {
  _session: Session
  filter: FilterState
  survey: SurveyState
}

interface OwnProps {
  currentQuestion: Question
  currentTopic: Topic
  count: number
  isOpenByDefault?: boolean
  showSegmentation?: boolean
  //Pour que le parent puisse garder son propre skeleton affiché tant que ce composant
  //charge encore ses messages (évite un flash quand il s'auto-ouvre au montage — cf isOpenByDefault)
  onLoadingChange?: (isLoading: boolean) => void
}

type Props = StateProps & OwnProps

const TAB_SUMMARY: string = "TAB_SUMMARY"
const TAB_ALL: string = "TAB_ALL"

const MODAL_EDIT_PROMPT: string = "MODAL_EDIT_PROMPT"
const MODAL_EDIT_TAGS: string = "MODAL_EDIT_TAGS"

function DashboardQuestionOpenWidget(props: Props) {
  const { t } = props

  //Current modal
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //Question active
  const [currentQuestion, setCurrentQuestion] = useState(props.currentQuestion)

  //Current tab (summary or all). Il not enough comment, TAB_ALL only is active
  const [currentTab, setCurrentTab] = useState<string>(TAB_ALL)

  //List of message to display for the question
  const [messages, setMessages] = useState<Message[]>([])

  //Open question
  const [open, setOpen] = useState<boolean>(props.isOpenByDefault ? true : false)

  //Custom prompt
  const [prompt, setPrompt] = useState(currentQuestion.prompt)
  const [isPromptCustom, setIsPromptCustom] = useState(currentQuestion.isPromptCustom)
  const [isPromptSaving, setIsPromptSaving] = useState(false)

  //Text for the search (for filtering in messages)
  const [search, setSearch] = useState<string>("")

  //Selected color for a filter (show only message with a specific color tag)
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null)

  //Status loading
  const [status, setStatus] = useState<string>(STATUS_LOADED)

  useEffect(() => {
    props.onLoadingChange?.(status === STATUS_LOADING)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  //List of messages
  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([])

  //Update messages to display
  useEffect(() => {
    if (selectedTag) {
      setDisplayedMessages(messages.filter((x) => x.color === selectedTag.color))
    } else if (search.length) {
      const insensitiveSearch = makeInsensitive(search)
      setDisplayedMessages(messages.filter((x) => makeInsensitive(x.text).indexOf(insensitiveSearch) > -1))
    } else {
      setDisplayedMessages(messages)
    }
  }, [messages, selectedTag, search])

  //Load messages
  //- when question is open
  //- when tab is switched
  //- when filters are changed
  useEffect(() => {
    async function load() {
      if (open) {
        setStatus(STATUS_LOADING)

        let lang = props._session.language // fallback par défaut

        if (props.survey.active.ProjectId) {
          // On récupère le projet complet
          const actionResult: any = await store.dispatch(projectFetchOne(props.survey.active.ProjectId))
          const project = actionResult.payload?.project ?? actionResult.payload ?? null
          if (project?.messageOptions?.commentPivotLanguage) {
            lang = project.messageOptions.commentPivotLanguage
          }
        }

        //Fetch all messages
        if (currentTab === TAB_ALL) {
          const response: any = await store.dispatch(
            fetchMessages(
              props.filter.dashboard,
              currentQuestion.aid,
              props.currentTopic.aid,
              undefined,
              undefined,
              lang
            )
          )

          if (!response.error) {
            setMessages(
              orderBy(
                response.map((x) => new Message(x)),
                "color"
              )
            )
          }
        }

        //End loading
        setStatus(STATUS_LOADED)
      }
    }

    load()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, open, props.filter.dashboard])

  //click on caret in order to open question
  function clickOpen() {
    setOpen(!open)
  }

  async function savePrompt() {
    if (isPromptCustom && (!prompt?.length || prompt === "")) {
      toast(t("question_open_prompt_not_defined"), { type: "error" })
    } else {
      setIsPromptSaving(true)
      const response: any = await store.dispatch(questionUpdatePrompt(currentQuestion.aid, isPromptCustom, prompt))

      if (!response.error) {
        const question = new Question(currentQuestion)
        question.prompt = prompt
        question.isPromptCustom = isPromptCustom
        setCurrentQuestion(question)
      }

      setTimeout(() => {
        setIsPromptSaving(false)
        if (!response.error) {
          setCurrentModal(null)
        }
      }, 250)
    }
  }

  function getCount(color: string) {
    return messages.filter((message: Message) => message.color && message.color.toUpperCase() === color).length
  }

  function displaySummaryTab() {
    return props.count > MESSAGE_CEIL_COUNT_SUMMARY && props._session.interfaceType !== "ADMIN_PARENT"
  }

  async function saveColors(tags: Tag[]) {
    const response: any = await store.dispatch(accountUpdateColors({ ...props._session.accountColors, tags }))
    if (!response.error) {
      store.dispatch(sessionEditAccountColors("tags", tags))
    }
  }

  //Set color for a message
  function setColor(message: Message, color: string) {
    message.color = color
    setMessages(messages.map((x: Message) => (x.aid === message.aid ? message : x)))

    if (props._session.authLevel > 0) {
      store.dispatch(messageUpdateColor(message.aid, color))
    }
  }

  //Set color in order to filter by tag
  //If click again : disable color
  function setColorFilter(tag: Tag | null) {
    setSelectedTag(!tag || (selectedTag && tag.color === selectedTag.color) ? null : tag)
  }

  const [showOnlyAlerts, setShowOnlyAlerts] = useState(false)
  //The alerts (comments judged critical, violent or hateful by an AI analysis) are offered only with the module "Résumés (IA)"
  const hasAtLeastOneAlert = props._session.getModuleObserver("summarize") && messages.some((msg) => msg.hasAlert)

  useEffect(() => {
    let filtered = messages
    if (selectedTag) {
      filtered = filtered.filter((x) => x.color === selectedTag.color)
    } else if (search.length) {
      const insensitiveSearch = makeInsensitive(search)
      filtered = filtered.filter((x) => makeInsensitive(x.text).indexOf(insensitiveSearch) > -1)
    }
    if (showOnlyAlerts) {
      filtered = filtered.filter((x) => x.hasAlert)
    }
    setDisplayedMessages(filtered)
  }, [messages, selectedTag, search, showOnlyAlerts])

  return !props._session.isTabDisplayed("/dashboard/messages") ? null : (
    <Card isWithoutPadding exportName={currentQuestion.label}>
      {currentModal === MODAL_EDIT_PROMPT && (
        <Modal
          status={isPromptSaving ? STATUS_SAVING : STATUS_LOADED}
          title={t("question_open_edit_prompt")}
          onClose={() => setCurrentModal(null)}
          onNext={() => savePrompt()}
          isCloseButtonVisible>
          <Checkbox
            active={isPromptCustom}
            text={t("question_open_edit_prompt_checkbox")}
            onClick={() => {
              if (!isPromptCustom && (prompt === "" || !prompt)) {
                setPrompt(t("dashboard_question_custom_prompt_default", { question: currentQuestion.label }))
              }
              setIsPromptCustom(!isPromptCustom)
            }}
          />

          {isPromptCustom && (
            <div style={{ marginTop: 24 }}>
              <p className="grey-t">Intitulé du prompt</p>

              <textarea
                className="text-area"
                style={{ height: "120px" }}
                onChange={(e) => setPrompt(e.target.value)}
                name="description"
                value={prompt}></textarea>
            </div>
          )}
        </Modal>
      )}

      {currentModal === MODAL_EDIT_TAGS && (
        <EditTagsModal
          tags={props._session.accountColors.tags}
          onClose={() => setCurrentModal(null)}
          onSave={saveColors}
        />
      )}

      <div className="_hover" onClick={clickOpen} style={{ padding: "20px 30px" }}>
        <div className="flex">
          <ListButton icon={open ? faCaretDown : faCaretRight} onClick={clickOpen} />

          <div>
            {currentQuestion.primaryId && (
              <div className="grey-t" style={{ marginBottom: "6px" }}>
                {t("question_primary")} : {currentQuestion.getQuestionPrimary(props.currentTopic.Questions).label}
              </div>
            )}

            <b>{currentQuestion.label}</b>
          </div>

          <Space />

          {props.count > 0 && (
            <div className="grey-t flex" style={{ minWidth: 48 }}>
              <Space />
              <FontAwesomeIcon icon={faComment} />
              &nbsp;
              {props.count}
            </div>
          )}
        </div>

        <div className="flex message-axis" style={{ color: props.currentTopic.Axis?.color }}>
          {props.currentTopic.label}
        </div>

        <div className="grey-t message-axis" style={{ fontSize: 12 }}>
          {props.showSegmentation &&
            getSegmentationPopulations(props.currentTopic.AxisId).map(
              (population: Population) => `${population.filterName}: ${population.name}`
            )}
        </div>
      </div>

      {open && (
        <>
          {displaySummaryTab() && (
            <div style={{ padding: "0px 30px" }} className="flex">
              {props._session.getModuleObserver("summarize") && (
                <Tabs
                  active={currentTab}
                  onClick={(tab: any) => (currentTab !== tab ? setCurrentTab(tab) : {})}
                  tabs={{
                    TAB_ALL: t("message_all"),
                    TAB_SUMMARY: t("message_synthesis")
                  }}
                />
              )}

              <Space />

              {props._session.getModuleObserver("summarize") &&
                currentTab === TAB_SUMMARY &&
                props._session.userRole === "ADMIN" && (
                  <div className="mt-20">
                    <Link onClick={() => setCurrentModal(MODAL_EDIT_PROMPT)}>{t("question_open_edit_prompt")}</Link>
                  </div>
                )}
            </div>
          )}

          {currentTab === TAB_SUMMARY ? (
            /*ChatGPT*/
            props._session.getModuleObserver("summarize") ? (
              <div style={{ padding: "10px 40px" }}>
                {status === STATUS_LOADING ? (
                  <DashboardQuestionOpenSummarySkeletonWidget />
                ) : (
                  <>
                    {messages.length < MESSAGE_CEIL_COUNT_SUMMARY ? (
                      <div>{t("message_synthesis_insufficient", { min: MESSAGE_CEIL_COUNT_SUMMARY })}</div>
                    ) : (
                      !isPromptSaving && (
                        <DashboardSummarizeWidget
                          comments={messages.map((message: Message) => message.text)}
                          currentQuestion={currentQuestion}
                        />
                      )
                    )}
                  </>
                )}

                <div className="height-20" />
              </div>
            ) : (
              <div className="flex justify-center px-5">
                <UpgradeWidget feature="summarize" withNoImages />
              </div>
            )
          ) : (
            <div style={{ marginBottom: "20px" }}>
              {messages.length >= MESSAGE_CEIL_COUNT_SUMMARY && (
                <DashboardMessagesCloudWidget
                  active={search}
                  currentQuestionAid={currentQuestion.aid!}
                  currentTopicAid={props.currentTopic.aid!}
                  onClick={(word: string) => setSearch(word)}
                />
              )}

              <PageLoader status={status} skeleton={<DashboardQuestionOpenSkeletonWidget />}>
                <div
                  className="flex"
                  style={{
                    marginLeft: "50px",
                    height: "110px"
                  }}>
                  <TextInput value={search} onChange={(e) => setSearch(e.value)} title={t("utils_search")} />

                  <Space />
                  {hasAtLeastOneAlert && (
                    <div className="mx-4">
                      <Chip
                        icon={showOnlyAlerts ? faBell : faExclamationTriangle}
                        color="orange"
                        isOutlineColored={!showOnlyAlerts}
                        isWithMargin={true}
                        onClick={() => setShowOnlyAlerts((v) => !v)}>
                        {showOnlyAlerts ? t("utils_show") : t("show_alert")}
                      </Chip>
                    </div>
                  )}

                  <div
                    style={{
                      paddingRight: "30px"
                    }}>
                    <p className="grey-t">{t("message_filter")}</p>

                    <div className="flex">
                      {props._session.accountColors.tags
                        .filter((tag: Tag) => getCount(tag.color) > 0)
                        .map((tag: Tag) => (
                          <div
                            key={tag.color}
                            data-tip={tag.name}
                            className="messages-colors _hover"
                            onClick={() => setColorFilter(tag)}
                            style={{ backgroundColor: tag.color }}>
                            {selectedTag && selectedTag.color === tag.color ? (
                              <div className="messages-colors-active" />
                            ) : (
                              <div style={{ textAlign: "center", color: "white", marginTop: 2 }}>
                                {getCount(tag.color)}
                              </div>
                            )}
                          </div>
                        ))}

                      {selectedTag ? (
                        <div
                          className="messages-colors _hover flex"
                          onClick={() => setColorFilter(null)}
                          style={{ border: "1px solid #8C8C8C" }}>
                          <FontAwesomeIcon icon={faTimes} />
                        </div>
                      ) : (
                        <div
                          className="messages-colors _hover flex"
                          onClick={() => {
                            setCurrentModal(MODAL_EDIT_TAGS)
                          }}
                          style={{ border: "1px solid #8C8C8C" }}>
                          <FontAwesomeIcon icon={faPen} />
                        </div>
                      )}
                    </div>

                    <ReactTooltip />
                  </div>
                </div>

                {selectedTag && (
                  <ListItem>
                    <Chip color={selectedTag.color} count={getCount(selectedTag.color)}>
                      {selectedTag.name}
                    </Chip>
                  </ListItem>
                )}

                {displayedMessages.map((message: Message, i: number) => (
                  <DashboardMessageWidget
                    key={i}
                    search={search}
                    id={(i + 1).toString()}
                    currentMessage={message}
                    currentQuestion={currentQuestion}
                    currentTopic={props.currentTopic}
                    onSetColor={(color) => setColor(message, color)}
                  />
                ))}
              </PageLoader>
            </div>
          )}
        </>
      )}
    </Card>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  filter: state.filter,
  survey: state.survey
})

export default connect(mapStateToProps)(withTranslation()(DashboardQuestionOpenWidget))
